<template>
  <div class="tasks-view">
    <h2>Задания</h2>
    
    <div class="tasks-container">
      <div 
        v-for="task in sortedTasks" 
        :key="task.id"
        class="task-item"
        :class="{ completed: task.completed }"
      >
        <div class="task-content">
          <div class="task-header">
            <div class="task-name">{{ task.name }}</div>
            <div class="task-reward">+{{ formatDisplay(task.reward) }} монет</div>
          </div>
          <div class="task-progress" v-if="task.requiredClicks || task.requiredReferrals">
            <div class="progress-text" v-if="task.requiredClicks">{{ getCurrentClicks() }} / {{ task.requiredClicks }}</div>
            <div class="progress-text" v-if="task.requiredReferrals">{{ getCurrentReferralCount() }} / {{ task.requiredReferrals }}</div>
            <div class="progress-bar" v-if="task.requiredClicks || task.requiredReferrals">
              <div class="progress-fill" :style="{ width: getTaskProgressPercentage(task) + '%' }"></div>
            </div>
          </div>
        </div>
        <div class="task-action">
          <button 
            v-if="!task.completed"
            class="task-button"
            @click="performTask(task)"
          >
            {{ task.actionText }}
          </button>
          <span v-else class="completed-badge">✓ Выполнено</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { updateBalance } from '../supabase';
import store, { updateBalance as updateGlobalBalance } from '../store.js';

export default {
  name: 'TasksView',
  props: {
    appBalance: {
      type: [Number, String],
      default: 0
    }
  },
  emits: ['balanceUpdated'],
  setup(props, { emit }) {
    // Загрузка состояния выполненных заданий из localStorage
    const loadCompletedTasks = () => {
      const saved = localStorage.getItem('completed_tasks');
      return saved ? JSON.parse(saved) : {};
    };
    
    const saveCompletedTask = (taskId) => {
      const completedTasks = loadCompletedTasks();
      completedTasks[taskId] = true;
      localStorage.setItem('completed_tasks', JSON.stringify(completedTasks));
    };
    
    const isTaskCompleted = (taskId) => {
      const completedTasks = loadCompletedTasks();
      return !!completedTasks[taskId];
    };
    
    const tasks = ref([
      // ЗАДАНИЕ 1 — КЛИКИ (Tap Tasks)
      { 
        id: 1, 
        name: 'Совершить 1 000 кликов', 
        reward: 2500, 
        completed: false,
        actionText: 'Проверить',
        type: 'click',
        requiredClicks: 1000,
        maxCompletions: 1,
        category: 'tap'
      },
      { 
        id: 2, 
        name: 'Совершить 5 000 кликов', 
        reward: 7500, 
        completed: false,
        actionText: 'Проверить',
        type: 'click',
        requiredClicks: 5000,
        maxCompletions: 1,
        category: 'tap'
      },
      { 
        id: 3, 
        name: 'Совершить 10 000 кликов', 
        reward: 14000, 
        completed: false,
        actionText: 'Проверить',
        type: 'click',
        requiredClicks: 10000,
        maxCompletions: 1,
        category: 'tap'
      },
      
      // ЗАДАНИЕ 3 — РЕФЕРАЛЫ
      { 
        id: 4, 
        name: 'Пригласить 1 реферала', 
        reward: 5000, 
        completed: false,
        actionText: 'Проверить',
        type: 'referral',
        requiredReferrals: 1,
        maxCompletions: 1,
        category: 'referral'
      },
      { 
        id: 5, 
        name: 'Пригласить 5 рефералов', 
        reward: 25000, 
        completed: false,
        actionText: 'Проверить',
        type: 'referral',
        requiredReferrals: 5,
        maxCompletions: 1,
        category: 'referral'
      },
      
      // Новое задание - Подписка на канал
      { 
        id: 7, 
        name: 'Подписаться на оф канал', 
        reward: 10000, 
        completed: false,
        actionText: 'Проверить',
        type: 'subscribe',
        maxCompletions: 1,
        category: 'other'
      }
    ]);

    // Инициализация состояния задач при загрузке компонента
    onMounted(() => {
      tasks.value.forEach(task => {
        task.completed = isTaskCompleted(task.id);
      });
    });

    // Сортировка заданий: невыполненные вверху, выполненные внизу
    const sortedTasks = computed(() => {
      return [...tasks.value].sort((a, b) => {
        // Невыполненные задания идут первыми (false < true)
        if (a.completed === b.completed) {
          return 0;
        }
        return a.completed ? 1 : -1;
      });
    });

    // Get current clicks from localStorage
    const getCurrentClicks = () => {
      const savedState = localStorage.getItem('astroGameState');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          return state.totalClicks || 0;
        } catch (e) {
          console.error('Failed to parse game state:', e);
          return 0;
        }
      }
      return 0;
    };
    
    // Get current referral count
    const getCurrentReferralCount = () => {
      const savedReferrals = localStorage.getItem('astro_referral_count');
      return savedReferrals ? parseInt(savedReferrals) : 0;
    };
    
    // Get progress percentage for click tasks
    const getProgressPercentage = (task) => {
      if (!task.requiredClicks) return 0;
      const currentClicks = getCurrentClicks();
      return Math.min(100, Math.floor((currentClicks / task.requiredClicks) * 100));
    };
    
    // Get referral progress percentage
    const getReferralProgressPercentage = (task) => {
      if (!task.requiredReferrals) return 0;
      const currentReferrals = getCurrentReferralCount();
      return Math.min(100, Math.floor((currentReferrals / task.requiredReferrals) * 100));
    };

    // Get task icon based on category
    const getTaskIcon = (task) => {
      switch(task.category) {
        case 'tap':
          return '🖱';
        case 'referral':
          return '👥';
        case 'other':
          return '⭐';
        default:
          return '⭐';
      }
    };

    // Get task progress percentage (for both click and referral tasks)
    const getTaskProgressPercentage = (task) => {
      if (task.type === 'click' && task.requiredClicks) {
        return getProgressPercentage(task);
      } else if (task.type === 'referral' && task.requiredReferrals) {
        return getReferralProgressPercentage(task);
      }
      return 0;
    };

    const formatDisplay = (num) => {
      if (typeof num !== 'number') num = Number(num) || 0;
      return Math.floor(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    // Общая функция завершения задания
    const completeTask = async (task) => {
      // Дополнительная проверка, не выполнено ли задание уже
      if (isTaskCompleted(task.id)) return;
      
      task.completed = true;
      saveCompletedTask(task.id);
      
      // Get current balance from props
      let currentBalance = 0;
      if (props.appBalance !== null && props.appBalance !== undefined && props.appBalance !== 'Loading...') {
        currentBalance = Number(props.appBalance);
      }
      
      console.log('=== TASK PERFORM START ===');
      console.log('Task:', task.name);
      console.log('Current appBalance from props:', props.appBalance);
      console.log('Parsed currentBalance:', currentBalance);
      
      // Get Telegram ID
      let finalTelegramId = null;
      
      if (window.Telegram?.WebApp?.initDataUnsafe?.user?.id) {
        finalTelegramId = window.Telegram.WebApp.initDataUnsafe.user.id.toString();
      } else {
        // Fallback to localStorage if Telegram ID not available
        const storedUserId = localStorage.getItem('astro_local_user');
        if (storedUserId) {
          finalTelegramId = storedUserId;
        }
      }
      
      // Calculate new balance
      const newBalance = currentBalance + task.reward;
      
      console.log('=== REWARD CALCULATION ===');
      console.log('Current balance:', currentBalance);
      console.log('Task reward:', task.reward);
      console.log('New balance:', newBalance);
      
      // Update global store balance
      updateGlobalBalance(newBalance);
      
      // Update balance locally first
      console.log('Emitting balanceUpdated with:', newBalance);
      emit('balanceUpdated', newBalance);
      
      // Also emit to window for debugging
      window.dispatchEvent(new CustomEvent('astro:balance-update', { 
        detail: { balance: newBalance } 
      }));
      
      // Try to update Supabase if Telegram ID available
      if (finalTelegramId) {
        try {
          await updateBalance(finalTelegramId, newBalance);
          console.log(`Task completed: ${formatDisplay(task.reward)} coins added to Supabase`);
        } catch (error) {
          console.error('Error updating balance in Supabase:', error);
          console.log('Balance updated locally despite Supabase error');
        }
      } else {
        console.log(`Task completed: ${formatDisplay(task.reward)} coins added locally (no Telegram ID)`);
        localStorage.setItem('astro_local_balance', newBalance.toString());
      }
      
      // Show success notification
      if (window.showNotification) {
        window.showNotification(
          'Задание выполнено!',
          `Награда: ${formatDisplay(task.reward)} монет получена`,
          'success'
        );
      } else {
        console.log(`🔔 Уведомление: Задание выполнено! - Награда: ${formatDisplay(task.reward)} монет получена`);
      }
    };

    // Функция проверки реферальных заданий
    const checkReferralTask = async (task) => {
      console.log('=== CHECKING REFERRAL TASK ===');
      console.log('Task:', task);
      
      const currentReferrals = getCurrentReferralCount();
      console.log('Current referrals:', currentReferrals);
      console.log('Required referrals:', task.requiredReferrals);
      
      if (currentReferrals >= task.requiredReferrals) {
        await completeTask(task);
      } else {
        if (window.showNotification) {
          window.showNotification(
            'Задание не выполнено',
            `Пригласите ${task.requiredReferrals} рефералов. Сейчас у вас: ${currentReferrals} рефералов.`,
            'error'
          );
        } else {
          console.log(`🔔 Уведомление: Задание не выполнено - Пригласите ${task.requiredReferrals} рефералов. Сейчас у вас: ${currentReferrals} рефералов.`);
        }
      }
    };

    // Выполнение задания
    const performTask = async (task) => {
      console.log('=== TASK PERFORM START ===');
      console.log('Task:', task.name);
      console.log('Current appBalance:', props.appBalance);
      
      // Prevent completing tasks multiple times
      if (task.completed || isTaskCompleted(task.id)) {
        if (window.showNotification) {
          window.showNotification(
            'Задание уже выполнено',
            'Это задание можно выполнить только один раз.',
            'warning'
          );
        } else {
          console.log('🔔 Уведомление: Задание уже выполнено - Это задание можно выполнить только один раз.');
        }
        return;
      }
      
      // Проверка максимального количества выполнений
      if (task.completionsCount >= task.maxCompletions) {
        if (window.showNotification) {
          window.showNotification(
            'Задание уже выполнено',
            'Это задание можно выполнить только один раз.',
            'warning'
          );
        } else {
          console.log('🔔 Уведомление: Задание уже выполнено - Это задание можно выполнить только один раз.');
        }
        return;
      }
      
      if (task.type === 'click') {
        // Проверка условий выполнения задания
        const savedState = localStorage.getItem('astroGameState');
        let totalClicks = 0;
        
        if (savedState) {
          try {
            const state = JSON.parse(savedState);
            totalClicks = state.totalClicks || 0;
          } catch (e) {
            console.error('Failed to parse game state:', e);
          }
        }
        
        console.log('=== TASK VALIDATION DEBUG ===');
        console.log('Required clicks:', task.requiredClicks);
        console.log('Current clicks from localStorage:', totalClicks);
        
        if (totalClicks >= task.requiredClicks) {
          await completeTask(task);
        } else {
          if (window.showNotification) {
            window.showNotification(
              'Задание не выполнено',
              `Выполните ${task.requiredClicks} кликов. Сейчас у вас: ${totalClicks} кликов.`,
              'error'
            );
          } else {
            console.log(`🔔 Уведомление: Задание не выполнено - Выполните ${task.requiredClicks} кликов. Сейчас у вас: ${totalClicks} кликов.`);
          }
        }
      } else if (task.type === 'referral') {
        await checkReferralTask(task);
      } else if (task.type === 'subscribe') {
        // Задание на подписку - считаем выполненным сразу
        await completeTask(task);
        if (window.showNotification) {
          window.showNotification(
            'Задание выполнено!',
            'Подписка на канал подтверждена',
            'success'
          );
        } else {
          console.log('🔔 Уведомление: Задание выполнено! - Подписка на канал подтверждена');
        }
      } else {
        console.warn('Unknown task type:', task.type);
      }
    };

    return {
      tasks,
      sortedTasks,
      formatDisplay,
      performTask,
      checkReferralTask,
      completeTask,
      getCurrentClicks,
      getCurrentReferralCount,
      getProgressPercentage,
      getReferralProgressPercentage,
      getTaskIcon,
      getTaskProgressPercentage
    };
  }
};
</script>

<style scoped>
.tasks-view {
  padding: 20px;
  padding-bottom: 100px;
  height: calc(100vh - 80px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #121212;
  /* Добавим градиентный фон */
  background: linear-gradient(135deg, #121212 0%, #1a1a1a 100%);
}

.tasks-view h2 {
  text-align: center;
  margin-bottom: 25px;
  font-size: 2rem;
  color: #e0e0e0;
  text-shadow: 0 0 15px rgba(76, 175, 80, 0.4);
  font-weight: 700;
  letter-spacing: 1px;
  position: relative;
}

.tasks-view h2::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  border-radius: 2px;
}

.tasks-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 400px;
  margin: 0 auto;
  padding: 0 10px;
}


.task-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px; /* Reduced from 18px */
  background: rgba(45, 45, 45, 0.9);
  border-radius: 15px;
  border: 1px solid rgba(80, 80, 80, 0.5);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
}

.task-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 100%;
  background: linear-gradient(90deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05));
  transition: width 0.3s ease;
}

.task-item:hover::before {
  width: 100%;
}

.task-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
  border-color: rgba(76, 175, 80, 0.4);
}

.task-item.completed {
  opacity: 0.8;
  border-color: rgba(76, 175, 80, 0.5);
  background: rgba(25, 45, 25, 0.5);
}

.task-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.task-name {
  font-weight: 500;
  font-size: 0.95rem; /* -15% */
  color: #e0e0e0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
  line-height: 1.4;
}

.task-reward {
  color: #D4AF37; /* More muted gold */
  font-size: 0.72rem; /* -20% */
  font-weight: 500;
  position: relative;
  z-index: 1;
  align-self: flex-start;
  transform: translateX(25%);
}

.task-action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 80px;
}

.task-button {
  padding: 6px 16px; /* -10-15% height */
  background: transparent;
  border: 2px solid #4CAF50; /* Changed from blue to green */
  border-radius: 10px;
  color: #4CAF50;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
  position: relative;
  overflow: hidden;
}

.task-button::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.task-button:active::after {
  width: 300px;
  height: 300px;
}

.task-button:hover:not(:disabled) {
  background: rgba(33, 150, 243, 0.1);
  border-color: #1565C0;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.task-icon {
  font-size: 1.2rem;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.8;
}

.task-progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.progress-text {
  font-size: 0.7rem;
  color: #aaa;
  font-weight: 400;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.task-button:disabled {
  background: linear-gradient(135deg, #424242 0%, #212121 100%);
  border-color: #333;
  color: #9e9e9e;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.completed-badge {
  color: #4CAF50;
  font-weight: bold;
  font-size: 0.9rem;
  text-shadow: 0 0 8px rgba(76, 175, 80, 0.4);
}

@media (max-width: 480px) {
  .tasks-view {
    padding: 15px;
    padding-bottom: 100px;
  }
  
  .task-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .task-action {
    margin-left: 0;
    margin-top: 10px;
    align-self: flex-end;
  }
}
</style>